/**
 * Contextual Post Navigation
 * Tracks browsing context (tag/category) and enables swipe/keyboard navigation
 * between posts in the same context with visual progress indicator
 */

(function () {
    'use strict';

    const STORAGE_KEY = 'daringBrowsingContext';
    const SWIPE_THRESHOLD = 50; // pixels
    const SWIPE_MAX_TIME = 500; // milliseconds

    /**
     * Normalize topic names (grouping CWC variants)
     */
    function normalizeTopicName(name) {
        if (!name) return name;
        const lowerName = name.toLowerCase();
        if (lowerName.includes('cwc-') ||
            lowerName === 'conversations with code' ||
            lowerName.startsWith('cwc ')) {
            return 'Conversations with Code';
        }
        return name;
    }

    // Check if we're on a post page
    const isPostPage = document.body.classList.contains('post-template');
    const isMobile = window.innerWidth <= 768;

    if (!isPostPage) {
        // If we're on a tag/archive page, store the context (only for desktop)
        if (!isMobile) {
            storeContextFromArchive();
        }
        return;
    }

    // Disable Mission Control / Contextual Nav completely on mobile
    if (isMobile) {
        return;
    }

    // We're on a post page - load context and set up navigation
    let context = loadContext();

    // If no context from browsing, generate default context from page data
    if (!context) {
        context = generateDefaultContext();
    }

    if (context) {
        // Check if context has valid data (either old single-topic or new multi-topic structure)
        const hasValidData = (context.posts && context.posts.length > 1) ||
            (context.topics && context.topics.length > 0);
        if (hasValidData) {
            initializeNavigation(context);
        }
    }

    /**
     * Generate default browsing context from current post page data
     * Uses Ghost's embedded post data to fetch related posts by primary tag
     */
    function generateDefaultContext() {
        // Try to get embedded post context data
        const contextDataEl = document.getElementById('post-context-data');
        if (!contextDataEl) {
            // console.log('No post context data found');
            return null;
        }

        let contextData;
        try {
            // Excerpts can carry raw newlines, which are illegal inside JSON
            // strings; between tokens they are just whitespace, so a space is safe.
            contextData = JSON.parse(contextDataEl.textContent.replace(/[\u0000-\u001F]+/g, ' '));
        } catch (e) {
            console.warn('Could not parse post context data:', e);
            return null;
        }

        if (!contextData.tagName) {
            // console.log('No primary tag found, cannot generate default context');
            return null;
        }

        // Get related posts from embedded data
        const relatedPostsContainer = document.getElementById('related-posts-data');
        if (!relatedPostsContainer) {
            // console.log('No related posts data found');
            return null;
        }

        const postCards = relatedPostsContainer.querySelectorAll('.hidden-post-card');
        if (postCards.length === 0) {
            // console.log('No related posts available');
            return null;
        }

        // Parse related posts
        const posts = Array.from(postCards).map(card => {
            return {
                url: card.dataset.url,
                title: card.dataset.title,
                image: card.dataset.image || null,
                excerpt: card.dataset.excerpt || ''
            };
        }).filter(post => post.url);

        // Find current post index
        const currentUrl = window.location.pathname;
        let currentPostIndex = posts.findIndex(post => post.url.includes(currentUrl));

        // If current post not in list, add it at the beginning
        if (currentPostIndex === -1) {
            posts.unshift(contextData.currentPost);
            currentPostIndex = 0;
        }

        // Need at least 2 posts for navigation to make sense
        if (posts.length < 2) {
            // console.log('Not enough posts for navigation');
            return null;
        }

        // Build context structure
        const context = {
            name: normalizeTopicName(contextData.tagName),
            url: '/archive/',
            topics: [{
                name: normalizeTopicName(contextData.tagName),
                posts: posts
            }],
            currentTopicIndex: 0,
            currentPostIndex: currentPostIndex,
            timestamp: Date.now()
        };

        // console.log('Generated default context:', context);
        return context;
    }

    /**
     * Store browsing context when clicking from tag/archive pages
     */
    function storeContextFromArchive() {
        // Listen for clicks on post links
        document.addEventListener('click', function (e) {
            const postLink = e.target.closest('a[href*="/"]');
            if (!postLink) return;

            // Check if we're on a tag page or archive page
            const isTagPage = document.body.classList.contains('tag-template');
            const isArchivePage = window.location.pathname.includes('/archive/') ||
                window.location.pathname.includes('/digest/') ||
                document.body.classList.contains('digest-archive-template');

            if (!isTagPage && !isArchivePage) return;

            // For archive page, find the closest section to determine context
            let postCards;
            let contextName = 'Posts';
            let contextUrl = window.location.pathname;

            if (isArchivePage) {
                // Find which section the clicked link is in
                const clickedSection = postLink.closest('.digest-month-section, .digest-topic-section, .digest-contributor-section');

                // Get ALL topic sections from the page
                const allSections = document.querySelectorAll('.digest-topic-section');
                const topics = [];
                let currentTopicIndex = 0;

                allSections.forEach((section, index) => {
                    const sectionTitle = section.querySelector('.digest-topic-title');
                    const sectionCards = section.querySelectorAll('.digest-post-card');

                    if (!sectionTitle || sectionCards.length === 0) return;

                    const topicName = normalizeTopicName(sectionTitle.textContent.trim());
                    const topicPosts = Array.from(sectionCards).map(card => {
                        const link = card.querySelector('a.digest-post-image-link, .digest-post-title a');
                        const title = card.querySelector('.digest-post-title');
                        const imgTag = card.querySelector('img.digest-post-image, img');
                        const image = imgTag ? (imgTag.currentSrc || imgTag.src) : null;

                        let excerpt = '';
                        const excerptEl = card.querySelector('.digest-post-excerpt, p');
                        if (excerptEl) {
                            excerpt = excerptEl.textContent.trim();
                        }

                        return {
                            url: link ? link.href : null,
                            title: title ? title.textContent.trim() : 'Untitled',
                            image: image,
                            excerpt: excerpt
                        };
                    }).filter(post => post.url);

                    // Merge topics with the same normalized name
                    const existingTopic = topics.find(t => t.name === topicName);
                    if (existingTopic) {
                        topicPosts.forEach(newPost => {
                            if (!existingTopic.posts.some(p => p.url === newPost.url)) {
                                existingTopic.posts.push(newPost);
                            }
                        });
                    } else {
                        topics.push({
                            name: topicName,
                            posts: topicPosts
                        });
                    }

                    // Track which topic was clicked
                    if (section === clickedSection) {
                        currentTopicIndex = topics.findIndex(t => t.name === topicName);
                        contextName = topicName;
                    }
                });

                // Store topics structure instead of flat posts
                const context = {
                    name: contextName,
                    url: contextUrl,
                    topics: topics,
                    currentTopicIndex: currentTopicIndex,
                    timestamp: Date.now()
                };

                try {
                    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(context));
                } catch (e) {
                    console.warn('Could not store browsing context:', e);
                }
                return; // Exit early since we handled storage
            } else if (isTagPage) {
                // Get all post cards from tag page
                postCards = document.querySelectorAll('.post-card');
                const tagHeader = document.querySelector('.post-card-large .post-card-title');
                if (tagHeader) {
                    contextName = tagHeader.textContent.trim();
                }
            } else {
                return;
            }

            // Map post cards to post data
            const posts = Array.from(postCards).map(card => {
                // Handle both post-card and digest-post-card structures
                const link = card.querySelector('a.post-card-image-link, a.post-card-content-link, a.digest-post-image-link, .digest-post-title a');
                const title = card.querySelector('.post-card-title, .digest-post-title');

                // Try to get featured image
                let image = null;

                // First try to find an actual img tag - digest uses class on img, not container
                const imgTag = card.querySelector('img.digest-post-image, .digest-post-image-link img, img.post-card-image, .post-card-image-link img, img');
                if (imgTag) {
                    image = imgTag.currentSrc || imgTag.src;
                }

                // Get excerpt - try multiple selectors
                let excerpt = '';
                const excerptEl = card.querySelector('.digest-post-excerpt, .post-card-excerpt, p');
                if (excerptEl) {
                    excerpt = excerptEl.textContent.trim();
                }

                return {
                    url: link ? link.href : null,
                    title: title ? title.textContent.trim() : 'Untitled',
                    image: image,
                    excerpt: excerpt
                };
            }).filter(post => post.url);

            if (posts.length === 0) return;

            // Store context
            const context = {
                name: normalizeTopicName(contextName),
                url: contextUrl,
                posts: posts,
                timestamp: Date.now()
            };

            try {
                sessionStorage.setItem(STORAGE_KEY, JSON.stringify(context));
            } catch (e) {
                console.warn('Could not store browsing context:', e);
            }
        });
    }

    /**
     * Load browsing context from storage
     */
    function loadContext() {
        try {
            const stored = sessionStorage.getItem(STORAGE_KEY);
            if (!stored) return null;

            const context = JSON.parse(stored);

            // Check if context is recent (within 30 minutes)
            const age = Date.now() - context.timestamp;
            if (age > 30 * 60 * 1000) {
                sessionStorage.removeItem(STORAGE_KEY);
                return null;
            }

            // Find current post across all topics
            const currentUrl = window.location.pathname;
            let currentTopicIndex = -1;
            let currentPostIndex = -1;

            if (context.topics) {
                // New multi-topic structure
                context.topics.forEach((topic, topicIdx) => {
                    const postIdx = topic.posts.findIndex(post =>
                        post.url && post.url.includes(currentUrl)
                    );
                    if (postIdx !== -1) {
                        currentTopicIndex = topicIdx;
                        currentPostIndex = postIdx;
                    }
                });

                if (currentTopicIndex === -1) return null;

                context.currentTopicIndex = currentTopicIndex;
                context.currentPostIndex = currentPostIndex;
            } else {
                // Old single-topic structure (backwards compat)
                const currentIndex = context.posts?.findIndex(post =>
                    post.url && post.url.includes(currentUrl)
                );

                if (currentIndex === -1) return null;
                context.currentIndex = currentIndex;
            }

            return context;

        } catch (e) {
            console.warn('Could not load browsing context:', e);
            return null;
        }
    }

    /**
     * Initialize navigation UI and interactions
     */
    function initializeNavigation(context) {
        // Handle both old (single topic) and new (multi-topic) structures
        let currentIndex, totalPosts, hasPrev, hasNext;

        if (context.topics) {
            // Multi-topic structure - nav within current topic
            const currentTopic = context.topics[context.currentTopicIndex];
            currentIndex = context.currentPostIndex;
            totalPosts = currentTopic.posts.length;
            hasPrev = currentIndex > 0;
            hasNext = currentIndex < totalPosts - 1;
        } else {
            // Old single-topic structure
            currentIndex = context.currentIndex;
            totalPosts = context.posts.length;
            hasPrev = currentIndex > 0;
            hasNext = currentIndex < totalPosts - 1;
        }

        // Set up mission control overview first (so we can access its state)
        const getMissionControlState = setupMissionControl(context, currentIndex);

        // Create navigation UI
        createNavigationUI(context, currentIndex, totalPosts, hasPrev, hasNext);

        // Set up keyboard navigation (with mission control state check)
        setupKeyboardNavigation(context, hasPrev, hasNext, getMissionControlState);

        // Set up touch/swipe navigation
        setupSwipeNavigation(context, hasPrev, hasNext);
    }

    /**
     * Create navigation UI elements - Floating bottom bar with liquid glass aesthetic
     */
    function createNavigationUI(context, currentIndex, totalPosts, hasPrev, hasNext) {
        // Create floating bottom navigation
        const nav = document.createElement('div');
        nav.className = 'contextual-nav-floating';

        // Calculate reading progress percentage (will be updated by scroll)
        const articleProgress = 0;

        // Build prev/next post info for hover tooltips
        let prevPost = null;
        let nextPost = null;
        if (context.topics) {
            const currentTopic = context.topics[context.currentTopicIndex];
            prevPost = hasPrev ? currentTopic.posts[currentIndex - 1] : null;
            nextPost = hasNext ? currentTopic.posts[currentIndex + 1] : null;
        } else {
            prevPost = hasPrev ? context.posts[currentIndex - 1] : null;
            nextPost = hasNext ? context.posts[currentIndex + 1] : null;
        }

        nav.innerHTML = `
            <div class="contextual-nav-glass">
                <div class="contextual-nav-content">
                    <button class="contextual-nav-btn contextual-nav-prev" 
                            ${!hasPrev ? 'disabled' : ''} 
                            aria-label="Previous post">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round">
                            <polyline points="15 18 9 12 15 6"></polyline>
                        </svg>
                        ${prevPost ? `<span class="contextual-nav-tooltip">${prevPost.title}</span>` : ''}
                    </button>
                    
                    <div class="contextual-nav-center">
                        <div class="contextual-nav-reading-progress">
                            <div class="contextual-nav-reading-bar" style="width: ${articleProgress}%"></div>
                        </div>
                        <div class="contextual-nav-info">
                            <span class="contextual-nav-position">${currentIndex + 1} of ${totalPosts}</span>
                            <span class="contextual-nav-divider">•</span>
                            <span class="contextual-nav-context"><a href="${context.url}">${context.name}</a></span>
                        </div>
                        <div class="contextual-nav-dots">
                            ${Array.from({ length: Math.min(totalPosts, 12) }).map((_, i) => {
            const dotIndex = Math.floor(i * totalPosts / Math.min(totalPosts, 12));
            const isActive = dotIndex === currentIndex;
            const isPast = dotIndex < currentIndex;
            return `<span class="contextual-nav-dot ${isActive ? 'active' : ''} ${isPast ? 'past' : ''}"></span>`;
        }).join('')}
                        </div>
                    </div>
                    
                    <button class="contextual-nav-btn contextual-nav-next" 
                            ${!hasNext ? 'disabled' : ''}
                            aria-label="Next post">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round">
                            <polyline points="9 18 15 12 9 6"></polyline>
                        </svg>
                        ${nextPost ? `<span class="contextual-nav-tooltip">${nextPost.title}</span>` : ''}
                    </button>
                </div>
            </div>
        `;

        document.body.appendChild(nav);

        // Add click handlers
        const prevBtn = nav.querySelector('.contextual-nav-prev');
        const nextBtn = nav.querySelector('.contextual-nav-next');

        if (hasPrev) {
            prevBtn.addEventListener('click', () => {
                if (context.topics) {
                    const currentTopic = context.topics[context.currentTopicIndex];
                    const prevPost = currentTopic.posts[currentIndex - 1];
                    if (prevPost && prevPost.url) window.location.href = prevPost.url;
                } else {
                    navigateToPost(context, currentIndex - 1);
                }
            });
        }

        if (hasNext) {
            nextBtn.addEventListener('click', () => {
                if (context.topics) {
                    const currentTopic = context.topics[context.currentTopicIndex];
                    const nextPost = currentTopic.posts[currentIndex + 1];
                    if (nextPost && nextPost.url) window.location.href = nextPost.url;
                } else {
                    navigateToPost(context, currentIndex + 1);
                }
            });
        }

        // Auto-hide behavior
        setupAutoHide(nav);

        // Reading progress tracking
        setupReadingProgress(nav);

        // Show initially, then fade after 3 seconds
        setTimeout(() => {
            nav.classList.add('visible');
        }, 300);

        setTimeout(() => {
            nav.classList.remove('visible');
        }, 3000);
    }

    /**
     * Setup auto-hide behavior on scroll
     */
    function setupAutoHide(nav) {
        let scrollTimeout;
        let lastScrollY = window.scrollY;

        window.addEventListener('scroll', function () {
            // Show on scroll
            nav.classList.add('visible');

            // Clear existing timeout
            clearTimeout(scrollTimeout);

            // Hide after 2 seconds of no scrolling
            scrollTimeout = setTimeout(() => {
                nav.classList.remove('visible');
            }, 2000);

            lastScrollY = window.scrollY;
        }, { passive: true });

        // Also show on mouse movement near bottom
        document.addEventListener('mousemove', function (e) {
            const windowHeight = window.innerHeight;
            if (e.clientY > windowHeight - 150) {
                nav.classList.add('visible');
                clearTimeout(scrollTimeout);
                scrollTimeout = setTimeout(() => {
                    nav.classList.remove('visible');
                }, 2000);
            }
        }, { passive: true });
    }

    /**
     * Track reading progress through article
     */
    function setupReadingProgress(nav) {
        const progressBar = nav.querySelector('.contextual-nav-reading-bar');
        if (!progressBar) return;

        window.addEventListener('scroll', function () {
            const article = document.querySelector('.zine-post-content, article');
            if (!article) return;

            const articleTop = article.offsetTop;
            const articleHeight = article.offsetHeight;
            const windowHeight = window.innerHeight;
            const scrollY = window.scrollY;

            // Calculate reading progress
            const progress = Math.max(0, Math.min(100,
                ((scrollY - articleTop + windowHeight / 2) / articleHeight) * 100
            ));

            progressBar.style.width = progress + '%';
        }, { passive: true });

        // Trigger once on load
        window.dispatchEvent(new Event('scroll'));
    }

    /**
     * Set up keyboard navigation (arrow keys) - only when mission control is NOT active
     */
    function setupKeyboardNavigation(context, hasPrev, hasNext, getMissionControlState) {
        document.addEventListener('keydown', function (e) {
            // Ignore if user is typing in an input
            if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') {
                return;
            }

            // Don't navigate articles if mission control is open
            if (getMissionControlState()) {
                return;
            }

            if (e.key === 'ArrowLeft' && hasPrev) {
                e.preventDefault();
                if (context.topics) {
                    const currentTopic = context.topics[context.currentTopicIndex];
                    const prevPost = currentTopic.posts[context.currentPostIndex - 1];
                    if (prevPost && prevPost.url) window.location.href = prevPost.url;
                } else {
                    navigateToPost(context, context.currentIndex - 1);
                }
            } else if (e.key === 'ArrowRight' && hasNext) {
                e.preventDefault();
                if (context.topics) {
                    const currentTopic = context.topics[context.currentTopicIndex];
                    const nextPost = currentTopic.posts[context.currentPostIndex + 1];
                    if (nextPost && nextPost.url) window.location.href = nextPost.url;
                } else {
                    navigateToPost(context, context.currentIndex + 1);
                }
            }
        });
    }

    /**
     * Set up swipe/touch navigation
     */
    function setupSwipeNavigation(context, hasPrev, hasNext) {
        let touchStartX = 0;
        let touchStartY = 0;
        let touchStartTime = 0;

        document.addEventListener('touchstart', function (e) {
            // Ignore if touching a link or button
            if (e.target.closest('a, button, input, textarea')) {
                return;
            }

            touchStartX = e.touches[0].clientX;
            touchStartY = e.touches[0].clientY;
            touchStartTime = Date.now();
        }, { passive: true });

        document.addEventListener('touchend', function (e) {
            if (touchStartX === 0) return;

            const touchEndX = e.changedTouches[0].clientX;
            const touchEndY = e.changedTouches[0].clientY;
            const touchEndTime = Date.now();

            const deltaX = touchEndX - touchStartX;
            const deltaY = touchEndY - touchStartY;
            const deltaTime = touchEndTime - touchStartTime;

            // Reset
            touchStartX = 0;
            touchStartY = 0;

            // Check if it's a horizontal swipe
            if (Math.abs(deltaX) < SWIPE_THRESHOLD) return;
            if (Math.abs(deltaY) > Math.abs(deltaX)) return; // Vertical swipe
            if (deltaTime > SWIPE_MAX_TIME) return; // Too slow

            // Swipe right (previous)
            if (deltaX > 0 && hasPrev) {
                navigateToPost(context, context.currentIndex - 1);
            }
            // Swipe left (next)
            else if (deltaX < 0 && hasNext) {
                navigateToPost(context, context.currentIndex + 1);
            }
        }, { passive: true });
    }

    /**
     * Setup mission control overview (spacebar)
     * Returns a function to check if mission control is active
     */
    function setupMissionControl(context, currentIndex) {
        let missionControlActive = false;
        let missionControlOverlay = null;
        let focusedCardIndex = currentIndex;
        let focusedTopicIndex = context.topics ? context.currentTopicIndex : 0;

        function showMissionControl() {
            if (missionControlActive) return;
            missionControlActive = true;

            // Create overlay
            missionControlOverlay = document.createElement('div');
            missionControlOverlay.className = 'mission-control-overlay';

            // Close button
            const closeBtn = document.createElement('button');
            closeBtn.className = 'mission-control-close';
            closeBtn.setAttribute('aria-label', 'Close overview');
            closeBtn.innerHTML = `
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <line x1="18" y1="6" x2="6" y2="18"></line>
                    <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
            `;
            closeBtn.addEventListener('click', hideMissionControl);
            missionControlOverlay.appendChild(closeBtn);

            // Keyboard legend
            const legend = document.createElement('div');
            legend.className = 'mission-control-legend';
            legend.innerHTML = `
                <div class="mission-control-legend-item">
                    <kbd>←</kbd> <kbd>→</kbd>
                    <span>Navigate posts</span>
                </div>
                <div class="mission-control-legend-item">
                    <kbd>↑</kbd> <kbd>↓</kbd>
                    <span>Switch topics</span>
                </div>
                <div class="mission-control-legend-item">
                    <kbd>M</kbd> / <kbd>Return</kbd>
                    <span>Go to post</span>
                </div>
                <div class="mission-control-legend-item">
                    <kbd>Esc</kbd>
                    <span>Close</span>
                </div>
            `;
            missionControlOverlay.appendChild(legend);

            // Create carousel container
            const carousel = document.createElement('div');
            carousel.className = 'mission-control-carousel';

            // Render topics (or fallback to single topic)
            const topics = context.topics || [{ name: context.name, posts: context.posts }];
            const currentPostIdx = context.currentPostIndex !== undefined ? context.currentPostIndex : currentIndex;

            topics.forEach((topic, topicIndex) => {
                const topicSection = document.createElement('div');
                topicSection.className = 'mission-control-topic-section';
                topicSection.dataset.topicIndex = topicIndex;

                if (topicIndex === focusedTopicIndex) {
                    topicSection.classList.add('focused');
                }

                // Topic header
                const topicHeader = document.createElement('div');
                topicHeader.className = 'mission-control-topic-header';
                topicHeader.innerHTML = `
                    <h2>${topic.name}</h2>
                    <p>${topic.posts.length} posts</p>
                `;
                topicSection.appendChild(topicHeader);

                // Topic cards grid
                const topicGrid = document.createElement('div');
                topicGrid.className = 'mission-control-topic-grid';

                topic.posts.forEach((post, postIndex) => {
                    const card = document.createElement('a');
                    card.href = post.url;
                    card.className = 'mission-control-card';
                    card.style.setProperty('--card-index', postIndex);
                    card.dataset.postIndex = postIndex;
                    card.dataset.topicIndex = topicIndex;

                    const isCurrentPost = (topicIndex === focusedTopicIndex && postIndex === currentPostIdx);
                    if (isCurrentPost) {
                        card.classList.add('current');
                        if (topicIndex === focusedTopicIndex) {
                            card.classList.add('focused');
                        }
                    }

                    const imageHtml = post.image ?
                        `<div class="mission-control-card-image" style="background-image: url('${post.image}')"></div>` :
                        `<div class="mission-control-card-image mission-control-card-image-placeholder"></div>`;

                    const excerptHtml = post.excerpt ?
                        `<div class="mission-control-card-excerpt">${post.excerpt.replace(/</g, '&lt;').replace(/>/g, '&gt;')}</div>` :
                        '';

                    card.innerHTML = `
                        ${imageHtml}
                        <div class="mission-control-card-content">
                            <div class="mission-control-card-number">${postIndex + 1}</div>
                            <div class="mission-control-card-title">${post.title}</div>
                            ${excerptHtml}
                            ${isCurrentPost ? '<div class="mission-control-current-badge">Reading now</div>' : ''}
                        </div>
                    `;

                    topicGrid.appendChild(card);
                });

                topicSection.appendChild(topicGrid);
                carousel.appendChild(topicSection);
            });

            missionControlOverlay.appendChild(carousel);
            document.body.appendChild(missionControlOverlay);

            // Scroll to focused topic instantly on initial load (no animation)
            setTimeout(() => {
                updateTopicFocus(true);
            }, 50);

            // Animate in
            requestAnimationFrame(() => {
                missionControlOverlay.classList.add('active');
            });

            // Click outside to close
            missionControlOverlay.addEventListener('click', (e) => {
                if (e.target === missionControlOverlay) {
                    hideMissionControl();
                }
            });
        }

        function updateTopicFocus(instant = false) {
            if (!missionControlOverlay) return;

            const carousel = missionControlOverlay.querySelector('.mission-control-carousel');
            const sections = missionControlOverlay.querySelectorAll('.mission-control-topic-section');

            sections.forEach((section, index) => {
                if (index === focusedTopicIndex) {
                    section.classList.add('focused');
                } else {
                    section.classList.remove('focused');
                }
            });

            // Force scroll to center the focused section
            const focusedSection = sections[focusedTopicIndex];
            if (focusedSection && carousel) {
                try {
                    // Calculate position to center the section
                    const sectionRect = focusedSection.getBoundingClientRect();
                    const carouselRect = carousel.getBoundingClientRect();
                    const sectionTop = focusedSection.offsetTop;
                    const sectionHeight = sectionRect.height;
                    const carouselHeight = carouselRect.height;

                    // Scroll to position that centers the section
                    const scrollTo = sectionTop - (carouselHeight / 2) + (sectionHeight / 2);

                    carousel.scrollTo({
                        top: scrollTo,
                        behavior: instant ? 'auto' : 'smooth'
                    });
                } catch (err) {
                    console.warn('Scroll error:', err);
                    // Fallback to scrollIntoView
                    focusedSection.scrollIntoView({ behavior: instant ? 'auto' : 'smooth', block: 'center' });
                }
            }

            // Update focused card within topic after scroll
            setTimeout(() => updateFocusedCard(), instant ? 100 : 200);
        }

        function hideMissionControl() {
            if (!missionControlActive || !missionControlOverlay) return;

            missionControlOverlay.classList.remove('active');
            setTimeout(() => {
                if (missionControlOverlay && missionControlOverlay.parentNode) {
                    missionControlOverlay.parentNode.removeChild(missionControlOverlay);
                }
                missionControlOverlay = null;
                missionControlActive = false;
            }, 300);
        }

        // Keyboard navigation
        document.addEventListener('keydown', (e) => {
            // Ignore if typing in input
            if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') {
                return;
            }

            // 'M' key behavior (Mission Control)
            if (e.code === 'KeyM' || e.key === 'm' || e.key === 'M') {
                e.preventDefault();
                if (missionControlActive) {
                    // If mission control is open, space navigates to focused card in focused topic
                    const focusedSection = missionControlOverlay?.querySelector(`.mission-control-topic-section[data-topic-index="${focusedTopicIndex}"]`);
                    if (focusedSection) {
                        const cards = focusedSection.querySelectorAll('.mission-control-card');
                        if (cards && cards[focusedCardIndex]) {
                            cards[focusedCardIndex].click();
                        }
                    }
                } else {
                    // If closed, space opens mission control
                    showMissionControl();
                }
                return;
            }

            // Return/Enter key - navigate to focused card (only when mission control is open)
            if ((e.key === 'Enter' || e.key === 'Return') && missionControlActive) {
                e.preventDefault();
                const focusedSection = missionControlOverlay?.querySelector(`.mission-control-topic-section[data-topic-index="${focusedTopicIndex}"]`);
                if (focusedSection) {
                    const cards = focusedSection.querySelectorAll('.mission-control-card');
                    if (cards && cards[focusedCardIndex]) {
                        cards[focusedCardIndex].click();
                    }
                }
                return;
            }

            // ESC to close
            if (e.key === 'Escape' && missionControlActive) {
                e.preventDefault();
                hideMissionControl();
                return;
            }

            // Arrow key navigation within mission control
            if (missionControlActive && missionControlOverlay) {
                const topics = context.topics || [{ posts: context.posts }];
                const currentTopic = topics[focusedTopicIndex];

                // Up/Down: Navigate between topics
                if (e.key === 'ArrowUp') {
                    e.preventDefault();
                    if (topics.length > 1) {
                        focusedTopicIndex = Math.max(focusedTopicIndex - 1, 0);
                        focusedCardIndex = 0; // Reset to first card in new topic
                        updateTopicFocus();
                    }
                } else if (e.key === 'ArrowDown') {
                    e.preventDefault();
                    if (topics.length > 1) {
                        focusedTopicIndex = Math.min(focusedTopicIndex + 1, topics.length - 1);
                        focusedCardIndex = 0; // Reset to first card in new topic
                        updateTopicFocus();
                    }
                }
                // Left/Right: Navigate between cards within focused topic
                else if (e.key === 'ArrowRight') {
                    e.preventDefault();
                    focusedCardIndex = Math.min(focusedCardIndex + 1, currentTopic.posts.length - 1);
                    updateFocusedCard();
                } else if (e.key === 'ArrowLeft') {
                    e.preventDefault();
                    focusedCardIndex = Math.max(focusedCardIndex - 1, 0);
                    updateFocusedCard();
                }
            }
        });

        function updateFocusedCard() {
            if (!missionControlOverlay) return;

            // Get cards from focused topic only
            const focusedSection = missionControlOverlay.querySelector(`.mission-control-topic-section[data-topic-index="${focusedTopicIndex}"]`);
            if (!focusedSection) return;

            const cards = focusedSection.querySelectorAll('.mission-control-card');
            cards.forEach((card, index) => {
                if (index === focusedCardIndex) {
                    card.classList.add('focused');
                    card.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
                } else {
                    card.classList.remove('focused');
                }
            });
        }

        // Return function to check mission control state
        return function () {
            return missionControlActive;
        };
    }

    /**
     * Navigate to a post by index
     */
    function navigateToPost(context, index) {
        const post = context.posts[index];
        if (!post || !post.url) return;

        // Add a smooth transition effect
        document.body.style.opacity = '0.7';
        document.body.style.transition = 'opacity 0.2s ease';

        // Navigate after brief delay for visual feedback
        setTimeout(() => {
            window.location.href = post.url;
        }, 150);
    }

})();
